import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, FileText, Cpu, Users, ArrowRight } from 'lucide-react';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { PublicFooter } from '@/components/PublicFooter';
import { PublicHeader } from '@/components/PublicHeader';
import { PUBLIC_CONTAINER } from '@/components/PublicContainer';
import SergeantTapInMotion from '@/components/in-motion/SergeantTapInMotion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_TAGS = ['p', 'br', 'b', 'strong', 'i', 'em', 'ul', 'ol', 'li'];

function safe(html) {
  return DOMPurify.sanitize(html || '', { ALLOWED_TAGS, ALLOWED_ATTR: [] });
}

// Parse the dynamic rich-text bullet list into individual <li> items so we can
// render them across two independent columns. Returns null if no <ul><li> found
// (caller falls back to rendering the raw sanitized HTML).
function parseBulletItems(html) {
  const clean = safe(html);
  if (!clean || typeof window === 'undefined' || !window.DOMParser) return null;
  const doc = new DOMParser().parseFromString(clean, 'text/html');
  const lis = doc.querySelectorAll('ul > li');
  if (!lis.length) return null;
  return Array.from(lis).map((li) => li.innerHTML);
}

function resolveImg(src) {
  if (!src) return '';
  // Pass through absolute external URLs and base64 data URIs as-is.
  // Only relative paths (e.g. legacy /api/uploads/...) need BACKEND_URL prefixed.
  if (src.startsWith('http') || src.startsWith('data:')) return src;
  return `${BACKEND_URL}${src}`;
}

const CARD_ICONS = [
  <FileText key="1" className="w-10 h-10 text-brand" strokeWidth={1.5} />,
  <Cpu key="2" className="w-10 h-10 text-brand" strokeWidth={1.5} />,
  <Users key="3" className="w-10 h-10 text-brand" strokeWidth={1.5} />,
];

function LandingPage() {
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/homepage-content`)
      .then((r) => setC(r.data))
      .catch(() => {});
    axios
      .get(`${BACKEND_URL}/api/public/news-research/featured`)
      .then((r) => setFeatured(r.data?.item || null))
      .catch(() => {});
  }, []);

  if (!c) return null;

  const prefersReduced = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Safe CTA navigation. Only #anchor, internal /paths, and http(s) URLs.
  // Rejects javascript:, data:, vbscript:, and malformed values.
  const handleCta = (dest) => {
    const d = (dest || '').trim();
    if (!d) return;
    if (d.startsWith('#')) {
      const el = document.getElementById(d.slice(1));
      if (el) el.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });
      return;
    }
    if (/^https?:\/\//i.test(d)) {
      window.location.href = d;
      return;
    }
    if (d.startsWith('/')) {
      navigate(d);
      return;
    }
    // Unsupported/unsafe scheme — ignore.
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* ===== HEADER (canonical public shell) ===== */}
      <PublicHeader />

      {/* ===== HERO ===== */}
      <section data-testid="hero-section" className="relative w-full flex items-center" style={{ minHeight: 520 }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${resolveImg(c.hero_image)})` }} />
        <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(117deg, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0.10) 100%)' }} />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 w-full">
          <div className="max-w-xl">
            <h1 data-testid="hero-headline" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-5">
              {c.hero_headline}
            </h1>
            <div data-testid="hero-body" className="text-base sm:text-lg text-white/85 leading-relaxed mb-4 rich-text" dangerouslySetInnerHTML={{ __html: safe(c.hero_body) }} />
            {c.hero_subtext && (
              <p data-testid="hero-subtext" className="text-sm text-white/65 leading-relaxed mb-8">{c.hero_subtext}</p>
            )}
            <button data-testid="hero-get-started-btn" onClick={() => handleCta(c.hero_primary_cta_url || '/auth')} className="btn-uppercase bg-brand hover:bg-brand-hover text-white px-8 py-3.5 rounded transition-colors text-sm">
              {c.hero_primary_cta_text}
            </button>
          </div>
        </div>
      </section>

      {/* ===== FEATURES — 3-card section ===== */}
      <section data-testid="features-section" className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 data-testid="features-title" className="text-2xl sm:text-3xl font-bold text-brand text-center mb-12">
            {c.feature_section_title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((n) => (
              <div key={n} data-testid={`feature-card-${n}`} className="text-center px-6 py-8">
                <div className="flex justify-center mb-5">{CARD_ICONS[n - 1]}</div>
                <h3 className="text-lg font-bold text-brand mb-3">{c[`feature_card_${n}_title`]}</h3>
                <div className="text-sm text-[#666] leading-relaxed rich-text" dangerouslySetInnerHTML={{ __html: safe(c[`feature_card_${n}_body`]) }} />
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <button data-testid="features-cta-btn" onClick={() => handleCta(c.hero_primary_cta_url || '/auth')} className="btn-uppercase bg-brand hover:bg-brand-hover text-white px-8 py-3 rounded transition-colors text-sm">
              {c.hero_primary_cta_text}
            </button>
          </div>
        </div>
      </section>

      {/* ===== INTERACTIVE "IN MOTION" NATIVE PRESENTATION (defaults to Job Seekers; internal Employers switch) ===== */}
      <SergeantTapInMotion
        initialAudience="seeker"
        seekerHref={c.job_seekers_cta_url || '/auth?mode=role-select'}
        employerHref={c.employers_cta_url || '/partner?source=employer'}
      />

      {/* ===== FOR SERVICE MEMBERS — Text left, Image right ===== */}
      <AudienceSection
        testId="job-seekers-section"
        anchorId="service-members"
        bg="bg-[#f9f9f9]"
        title={c.job_seekers_title}
        description={c.job_seekers_short_description}
        contentArea={c.job_seekers_content_area}
        image={resolveImg(c.job_seekers_image)}
        imageAlt="Service members and military families"
        imageTestId="job-seekers-image"
        onCta={() => handleCta(c.job_seekers_cta_url || '/auth?mode=role-select')}
        ctaText={c.job_seekers_cta_text || 'Start My Free Transition'}
        layout="text-left"
      />

      {/* ===== WHY MILITARY TALENT MATTERS ===== */}
      <TalentSection c={c} />

      {/* ===== FOR EMPLOYERS — Image left, Text right ===== */}
      <AudienceSection
        testId="employers-section"
        anchorId="employers"
        bg="bg-white"
        title={c.employers_title}
        description={c.employers_short_description}
        contentArea={c.employers_content_area}
        image={resolveImg(c.employers_image)}
        imageAlt="Employers"
        imageTestId="employers-image"
        onCta={() => handleCta(c.employers_cta_url || '/partner?source=employer')}
        ctaText={c.employers_cta_text || 'Explore Employer Participation'}
        layout="image-left"
      />

      {/* ===== FOR COMMUNITIES & WORKFORCE LEADERS — Text left, Image right ===== */}
      <AudienceSection
        testId="communities-section"
        anchorId="communities"
        bg="bg-[#f9f9f9]"
        title={c.communities_title}
        description={c.communities_short_description}
        contentArea={c.communities_content_area}
        image={resolveImg(c.communities_image)}
        imageAlt="Communities and workforce leaders"
        imageTestId="communities-image"
        onCta={() => handleCta(c.communities_cta_url || '/partner?source=community')}
        ctaText={c.communities_cta_text || 'Discuss a Regional Pilot'}
        layout="text-left"
      />

      {/* ===== WHY SERGEANT TAP EXISTS ===== */}
      <MissionSection c={c} />

      {/* ===== LATEST FROM SERGEANT TAP (data-driven featured News & Research) ===== */}
      {featured && (
        <section data-testid="latest-from-section" className="py-16 md:py-20 bg-white">
          <div className={PUBLIC_CONTAINER}>
            <p className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-4">Latest from Sergeant TAP</p>
            <Link
              to={`/news-research/${featured.slug}`}
              data-testid="latest-from-card"
              className="block rounded-2xl border border-slate-200 hover:border-brand/40 hover:shadow-md transition-all p-7 md:p-9"
            >
              <span className="text-[11px] font-bold tracking-wider uppercase text-brand">{featured.category}</span>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold text-slate-900 leading-snug">{featured.title}</h2>
              {featured.summary && <p className="mt-4 text-[15px] md:text-base text-slate-600 leading-relaxed">{featured.summary}</p>}
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
                Read the Findings <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </section>
      )}

      {/* ===== BOTTOM CTA ===== */}
      <section data-testid="bottom-cta-section" className="py-16 md:py-20 bg-[#f4f4f4]">
        <div className={`${PUBLIC_CONTAINER} text-center`}>
          <h2 data-testid="bottom-cta-headline" className="text-2xl sm:text-3xl font-bold text-[#222] mb-5">
            {c.bottom_cta_headline}
          </h2>
          <div data-testid="bottom-cta-body" className="text-[#555] leading-relaxed mb-8 rich-text" dangerouslySetInnerHTML={{ __html: safe(c.bottom_cta_body) }} />
          <button data-testid="cta-get-started-btn" onClick={() => handleCta(c.bottom_cta_button_url || '/auth')} className="btn-uppercase bg-brand hover:bg-brand-hover text-white px-8 py-3.5 rounded transition-colors text-sm">
            {c.bottom_cta_button_text}
          </button>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <PublicFooter footerText={c.footer_text} />
    </div>
  );
}

/* Reusable audience section (Job Seekers / Employers / Communities) */
function AudienceSection({ testId, anchorId, bg, title, description, contentArea, image, imageAlt, imageTestId, onCta, ctaText, layout }) {
  const isImageLeft = layout === 'image-left';

  const bulletItems = parseBulletItems(contentArea);
  const midpoint = bulletItems ? Math.ceil(bulletItems.length / 2) : 0;
  const leftItems = bulletItems ? bulletItems.slice(0, midpoint) : [];
  const rightItems = bulletItems ? bulletItems.slice(midpoint) : [];

  const textBlock = (
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold text-brand mb-4">{title}</h2>
      <p className="text-[#555] leading-relaxed mb-6">{description}</p>
      {bulletItems ? (
        <div className="text-sm text-[#444] leading-relaxed mb-8 audience-content audience-bullet-columns">
          <ul>
            {leftItems.map((html, i) => (
              <li key={`l-${i}`} dangerouslySetInnerHTML={{ __html: html }} />
            ))}
          </ul>
          <ul>
            {rightItems.map((html, i) => (
              <li key={`r-${i}`} dangerouslySetInnerHTML={{ __html: html }} />
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-sm text-[#444] leading-relaxed mb-8 rich-text audience-content" dangerouslySetInnerHTML={{ __html: safe(contentArea) }} />
      )}
      <button data-testid={`${testId}-cta-btn`} onClick={onCta} className="btn-uppercase bg-brand hover:bg-brand-hover text-white px-7 py-3 rounded transition-colors text-sm">
        {ctaText || 'Get Started'}
      </button>
    </div>
  );

  const imageBlock = (
    <div className="flex justify-center">
      <img data-testid={imageTestId} src={image} alt={imageAlt} loading="lazy" className="rounded shadow-lg max-w-full object-cover" style={{ maxHeight: 480 }} />
    </div>
  );

  return (
    <section id={anchorId} data-testid={testId} className={`homepage-anchor py-16 md:py-20 ${bg}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {isImageLeft ? (
            <>
              <div className="order-2 md:order-1">{imageBlock}</div>
              <div className="order-1 md:order-2">{textBlock}</div>
            </>
          ) : (
            <>
              {textBlock}
              {imageBlock}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

/* Why Military Talent Matters — 14 values from content fields, 3/2/1 columns */
function TalentSection({ c }) {
  const values = [];
  for (let i = 1; i <= 14; i++) {
    const title = c[`talent_value_${i}_title`];
    const description = c[`talent_value_${i}_description`];
    if (title || description) values.push({ i, title, description });
  }
  if (values.length === 0) return null;

  return (
    <section id="why-military-talent" data-testid="talent-section" className="homepage-anchor py-16 md:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 data-testid="talent-title" className="text-2xl sm:text-3xl font-bold text-brand mb-4">
          {c.talent_section_title}
        </h2>
        {c.talent_section_intro && (
          <p data-testid="talent-intro" className="text-[#555] leading-relaxed mb-10 max-w-3xl">
            {c.talent_section_intro}
          </p>
        )}
        <div className="talent-grid">
          {values.map((v) => (
            <div key={v.i} data-testid={`talent-value-${v.i}`} className="talent-item">
              {v.title && <h3 className="text-base font-bold text-[#222] mb-1">{v.title}</h3>}
              {v.description && <p className="text-sm text-[#666] leading-relaxed">{v.description}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* Why Sergeant TAP Exists — institutional mission, no founder details */
function MissionSection({ c }) {
  if (!c.mission_section_title && !c.mission_section_body) return null;
  return (
    <section id="why-sergeant-tap" data-testid="mission-section" className="homepage-anchor py-16 md:py-20 bg-white">
      <div className={PUBLIC_CONTAINER}>
        <h2 data-testid="mission-title" className="text-2xl sm:text-3xl font-bold text-brand mb-5">
          {c.mission_section_title}
        </h2>
        <div
          data-testid="mission-body"
          className="text-[#555] leading-relaxed rich-text"
          dangerouslySetInnerHTML={{ __html: safe(c.mission_section_body) }}
        />
      </div>
    </section>
  );
}

export default LandingPage;
